import React, { FC, useEffect, useState } from "react";
import { Stack, Button } from "@mui/material";
import SearchInput from "../SearchInput/SearchInput";
import DateRangeFilter from "../DateRangeFilter/DateRangeFilter";
import SearchIcon from "@mui/icons-material/Search";
import { IHomeFilter } from "../../common/interfaces/home.interface";
import "./HomeTableHeader.scss";
import { useFilterStore } from "../../store/filtersStore";
import CustomAutoSelectInput from "../CustomAutocompleteInput/CustomAutocompleteInput";
import CustomSelectInput from "../CustomSelectInputs/CustomSelectInput";
import {
  createToastNotification,
  getSignificanceLevelLabel,
  getStatusLabel,
  getUnitType,
  mapLabels,
} from "../../utilities/helpers";
import {
  fetchAuthors,
  fetchBibliographicUnitType,
  fetchOtherResponsibilities,
} from "../../api/filters";
import { fetchInstitutions } from "../../api/institutions";
import { LetterTranslator } from "../../utilities/letter-translator";
import CloseIcon from "@mui/icons-material/Close";

interface TableHeaderProps {
  getCulturalProperties: () => Promise<void>;
  setSearchFilters: React.Dispatch<React.SetStateAction<IHomeFilter>>;
  showBtn?: boolean;
  isCentralRegistry?: boolean;
  setIsBibliographicUnitSelected?: React.Dispatch<React.SetStateAction<boolean>>;
  resetKey: number;
  setResetKey: React.Dispatch<React.SetStateAction<number>>;
}

const HomeTableHeader: FC<TableHeaderProps> = ({
  getCulturalProperties,
  setSearchFilters,
  showBtn,
  isCentralRegistry,
  setIsBibliographicUnitSelected,
  resetKey,
  setResetKey,
}) => {
  const {
    holders,
    institutions,
    authors,
    otherResponsibilities,
    yearsOfProclamation,
    proclaimedCulturalAssets,
    significanceLevel,
    statuses,
  } = useFilterStore();

  const allSignificanceLevels = significanceLevel.significanceLevels || [];
  const allStatuses = statuses.recordStatus || [];

  const allSignificanceLevelsLabels = mapLabels(allSignificanceLevels, getSignificanceLevelLabel);
  const allStatusLabels = mapLabels(allStatuses, getStatusLabel);
  const [isReady, setIsReady] = useState(false);
  const handleSearchChange = (field: string, value: any) => {
    if (isReady) {
      setSearchFilters((prevFilters) => {
        const updatedFilters = {
          ...prevFilters,
          [field]: value,
        };
        localStorage.setItem("searchFilters", JSON.stringify(updatedFilters));
        return updatedFilters;
      });
    }

    if (field === "itemType") {
      const isBibliographicUnitSelected = value.includes("bibliographicUnit");
      const isSpecimenSelected = value.includes("specimen");

      if (setIsBibliographicUnitSelected) {
        setIsBibliographicUnitSelected(isBibliographicUnitSelected && !isSpecimenSelected);
      }
    }
  };

  const unitTypeOptions = [
    { value: "bibliographicUnit", label: "Библиографски" },
    { value: "specimen", label: "Примерак" },
  ];
  const [bibliographicType, setBibliographicType] = useState<{ types: string[] }>({ types: [] });
  const allBibliographicType = bibliographicType?.types || [];
  const bibliographicTypeLabels =
    allBibliographicType.length > 0 ? mapLabels(allBibliographicType, getUnitType) : [];

  const getBibliographicType = async () => {
    try {
      const response = await fetchBibliographicUnitType();
      setBibliographicType(response);
    } catch (error: any) {
      createToastNotification(error, error?.response?.data?.message);
    }
  };

  useEffect(() => {
    getBibliographicType();
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isCentralRegistry) {
      const savedFilters = localStorage.getItem("searchFilters");
      if (savedFilters) {
        setSearchFilters(JSON.parse(savedFilters));
      }
    }
  }, []);

  const handleResetFilters = () => {
    localStorage.setItem("searchFilters", JSON.stringify({}));
    setSearchFilters({});
    setResetKey((prev) => prev + 1);
  };

  return (
    <>
      <Stack className="filter-section" alignItems="center" spacing={1} direction="row">
        <SearchInput
          key={resetKey}
          icon
          setSearchValue={(value) => handleSearchChange("title", value)}
          searchTitle="Претражи по наслову"
          initialValue={
            isCentralRegistry && JSON.parse(localStorage.getItem("searchFilters") || "{}")?.title
          }
        />
        <CustomAutoSelectInput
          key={`author-${resetKey}`}
          setSearchValue={(value) => handleSearchChange("author", value)}
          searchTitle="Претражи по аутору"
          optionsData={authors}
          getOptionLabel={(author) => author.authorName}
          getOptionId={(author) => author.authorID}
          initialValue={
            isCentralRegistry && JSON.parse(localStorage.getItem("searchFilters") || "{}")?.author
          }
          fetchOptions={async (input) => {
            const searchLatin = LetterTranslator.cyr2lat(input);
            const searchCyrillic = LetterTranslator.lat2cyr(input);

            const response = await fetchAuthors(1, 50, input);
            return response.authors.filter((author: { authorName: string }) => {
              const name = author.authorName.toLowerCase();
              return (
                name.includes(input.toLowerCase()) ||
                name.includes(searchLatin.toLowerCase()) ||
                name.includes(searchCyrillic.toLowerCase())
              );
            });
          }}
        />
        <SearchInput
          key={`place-${resetKey}`}
          icon
          setSearchValue={(value) => handleSearchChange("placeOfIssue", value)}
          searchTitle="Претражи по месту издања"
          initialValue={
            isCentralRegistry &&
            JSON.parse(localStorage.getItem("searchFilters") || "{}")?.placeOfIssue
          }
        />
        <CustomSelectInput
          key={`significanceLevel-${resetKey}`}
          values={allSignificanceLevelsLabels}
          preselectedValueIds={
            isCentralRegistry &&
            JSON.parse(localStorage.getItem("searchFilters") || "{}")?.significanceLevel
          }
          label="Ниво значаја културног добра"
          onChange={(value) => handleSearchChange("significanceLevel", value)}
          width="100%"
          multiple={true}
        />
      </Stack>
      <Stack className="filter-section" direction="row" spacing={1}>
        <CustomAutoSelectInput
          key={`year-${resetKey}`}
          width="30%"
          setSearchValue={(value) => handleSearchChange("yearOfProclamation", value.map(String))}
          optionsData={yearsOfProclamation}
          getOptionLabel={(option) => (option ? option.toString() : "{}")}
          getOptionId={(option) => option}
          searchTitle="Година проглашења"
          initialValue={
            isCentralRegistry &&
            JSON.parse(localStorage.getItem("searchFilters") || "{}")?.yearOfProclamation
          }
        />
        <CustomAutoSelectInput
          key={`holder-${resetKey}`}
          width="30%"
          setSearchValue={(value) => handleSearchChange("holder", value)}
          optionsData={holders}
          getOptionLabel={(option) => option.holderName}
          getOptionId={(option) => option.holderID}
          searchTitle="Сопственик"
          initialValue={
            isCentralRegistry && JSON.parse(localStorage.getItem("searchFilters") || "{}")?.holder
          }
        />
        <DateRangeFilter
          key={`dateFrom-${resetKey}`}
          onChange={({ field, value }) => handleSearchChange(field, value)}
          fieldNames={{ from: "yearOfPublicationFrom", to: "yearOfPublicationTo" }}
          labels={{ from: "Година издања (од)", to: "Година издања (до)" }}
          initialValue={
            isCentralRegistry
              ? {
                  from: JSON.parse(localStorage.getItem("searchFilters") || "{}")
                    ?.yearOfPublicationFrom,
                  to: JSON.parse(localStorage.getItem("searchFilters") || "{}")
                    ?.yearOfPublicationTo,
                }
              : undefined
          }
        />
        <CustomAutoSelectInput
          key={`dateTo-${resetKey}`}
          width="30%"
          setSearchValue={(value) => handleSearchChange("otherResponsibilities", value)}
          searchTitle="Претражи по осталим одговорностима"
          optionsData={otherResponsibilities}
          getOptionLabel={(resp) => resp.name}
          getOptionId={(resp) => resp.id}
          initialValue={
            isCentralRegistry &&
            JSON.parse(localStorage.getItem("searchFilters") || "{}")?.otherResponsibilities
          }
          fetchOptions={async (input) => {
            const searchLatin = LetterTranslator.cyr2lat(input);
            const searchCyrillic = LetterTranslator.lat2cyr(input);

            const response = await fetchOtherResponsibilities(1, 50, input);
            return response.responsibilities.filter((resp: { name: string }) => {
              const name = resp.name.toLowerCase();
              return (
                name.includes(input.toLowerCase()) ||
                name.includes(searchLatin.toLowerCase()) ||
                name.includes(searchCyrillic.toLowerCase())
              );
            });
          }}
        />
      </Stack>
      <Stack className="filter-section" direction="row" spacing={1} mb={isCentralRegistry ? 0 : 2}>
        <CustomSelectInput
          key={`bibliographic-${resetKey}`}
          values={bibliographicTypeLabels}
          label="Врста библиографске грађе"
          onChange={(value) => handleSearchChange("unitType", value)}
          width="24.5%"
          multiple={true}
          preselectedValueIds={
            isCentralRegistry && JSON.parse(localStorage.getItem("searchFilters") || "{}")?.unitType
          }
        />
        {!isCentralRegistry && (
          <CustomSelectInput
            key={`status-${resetKey}`}
            values={allStatusLabels}
            label="Статус културног добра"
            onChange={(value) => handleSearchChange("status", value)}
            width="20.5%"
            multiple={true}
            preselectedValueIds={JSON.parse(localStorage.getItem("searchFilters") || "{}")?.status}
          />
        )}
        {isCentralRegistry && (
          <CustomSelectInput
            key={`recordStatus-${resetKey}`}
            values={allStatusLabels}
            label="Статус културног добра"
            onChange={(value) => handleSearchChange("recordStatus", value)}
            width="17%"
            multiple={true}
            preselectedValueIds={
              isCentralRegistry &&
              JSON.parse(localStorage.getItem("searchFilters") || "{}")?.recordStatus
            }
          />
        )}
        <CustomAutoSelectInput
          width="33%"
          key={`institution-${resetKey}`}
          setSearchValue={(value) => handleSearchChange("institution", value)}
          optionsData={institutions}
          getOptionLabel={(option) => option.institutionName}
          getOptionId={(option) => option.institutionID}
          searchTitle="Надлежна установа заштите која врши упис"
          initialValue={
            isCentralRegistry &&
            JSON.parse(localStorage.getItem("searchFilters") || "{}")?.institution
          }
          fetchOptions={async (input) => {
            const searchLatin = LetterTranslator.cyr2lat(input);
            const searchCyrillic = LetterTranslator.lat2cyr(input);

            const response = await fetchInstitutions(input);
            return response.filter((institution: { institutionName: string }) => {
              const name = institution.institutionName.toLowerCase();
              return (
                name.includes(input.toLowerCase()) ||
                name.includes(searchLatin.toLowerCase()) ||
                name.includes(searchCyrillic.toLowerCase())
              );
            });
          }}
        />
        <CustomAutoSelectInput
          key={`lists-${resetKey}`}
          width="25%"
          setSearchValue={(value) => handleSearchChange("lists", value)}
          optionsData={proclaimedCulturalAssets}
          getOptionLabel={(option) => option.name}
          getOptionId={(option) => option.id}
          searchTitle="Листа проглашених културних добара"
          initialValue={
            isCentralRegistry && JSON.parse(localStorage.getItem("searchFilters") || "{}")?.lists
          }
        />
        {showBtn && (
          <Button
            startIcon={<SearchIcon />}
            size="large"
            variant="contained"
            className="filter-section__search-button"
            onClick={getCulturalProperties}
          >
            Претражи
          </Button>
        )}
        {!isCentralRegistry && (
          <Button variant="contained" onClick={handleResetFilters}>
            <CloseIcon />
          </Button>
        )}
        {isCentralRegistry && (
          <CustomSelectInput
            key={`itemType-${resetKey}`}
            values={unitTypeOptions}
            label="Тип записа"
            onChange={(value) => handleSearchChange("itemType", value)}
            width="17%"
            multiple={true}
            preselectedValueIds={
              isCentralRegistry &&
              JSON.parse(localStorage.getItem("searchFilters") || "{}")?.itemType
            }
          />
        )}
      </Stack>
    </>
  );
};

export default HomeTableHeader;
